import {
  Mail,
  Phone,
  MapPin,
  Share2, User, MessageCircle,
} from "lucide-react";
import { useState } from "react";
import { db } from "../../firebase";
import {
  doc, setDoc, serverTimestamp
} from "firebase/firestore";


function ContactInfoItem({ icon, label, values }) {
  const Icon = icon;

  return (
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center">
        <Icon className="text-gold w-4 h-4" />
      </div>

      <div>
        <p className="font-manrope text-[11px] tracking-[0.14em] text-white/55 mb-1">
          {label}
        </p>

        <div className="flex flex-col gap-1">
          {values.map((value, index) => {
            const isEmail = value.includes("@");
            const isPhone = value.includes("+");

            return (
              <a
                key={index}
                href={
                  isEmail
                    ? `mailto:${value}`
                    : isPhone
                      ? `tel:${value.replace(/\s/g, "")}`
                      : undefined
                }
                className="font-manrope font-semibold text-white text-[15px] hover:text-gold transition-colors duration-200"
              >
                {value}
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="font-manrope text-label-caps text-on-surface-variant">{label}</label>
      {children}
    </div>
  );
}

const inputClass =
  "bg-surface-container-low border-0 border-b-2 border-outline-variant " +
  "focus:border-gold focus:ring-0 focus:outline-none " +
  "rounded-none px-0 py-3 font-manrope text-body-md text-on-surface transition-all w-full";

const contactGroups = [
  {
    icon: Mail,
    label: "EMAIL US",
    values: [
      "info@greatcalcy.co.in",
      "prashant@greatcalcy.co.in",
      "dhara@greatcalcy.co.in",
    ],
  },
  {
    icon: Phone,
    label: "INDIA",
    values: ["+91 99672 90235"],
  },
  {
    icon: Phone,
    label: "CANADA",
    values: ["+1 403 829 9209"],
  },
  {
    icon: MapPin,
    label: "OFFICES",
    values: ["Mumbai, India • Alberta, Canada"],
  },
];

export default function ContactSection() {

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    company: "",
    region: "India",
    service: "Bookkeeping",
    message: "",
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const docId = Date.now().toString();

      await setDoc(doc(db, "contacts", docId), {
        id: docId,
        ...formData,
        createdAt: serverTimestamp(),
      });

      alert("Inquiry submitted successfully!");

      setFormData({
        fullName: "",
        email: "",
        company: "",
        region: "India",
        service: "Bookkeeping",
        message: "",
      });
    } catch (error) {
      console.error(error);
      alert("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 max-w-[1280px] mx-auto px-6" id="contact">
      {/* Section header */}
      <div className="text-center mb-14">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="h-px w-8 bg-gold" />
          <span className="font-manrope text-label-caps text-gold">GET IN TOUCH</span>
          <div className="h-px w-8 bg-gold" />
        </div>
        <h2 className="font-manrope text-h1 text-navy">Ready to Optimize Your Finances?</h2>
      </div>

      <div className="bg-navy rounded-[2rem] overflow-hidden shadow-2xl flex flex-col lg:flex-row">
        {/* Left: contact info */}
        <div className="lg:w-2/5 p-12 flex flex-col justify-between relative overflow-hidden">
          {/* Decorative circle */}
          <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-gold/5 pointer-events-none" />
          <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white/5 pointer-events-none" />

          <div className="relative z-10">
            <p className="font-manrope text-on-primary-container text-body-md mb-10 max-w-xs">
              Connect with our experts for a tailored analysis of your accounting and tax needs across India, Canada, and the USA.
            </p>
            <div className="space-y-7">
              {contactGroups.map((group) => (
                <ContactInfoItem key={group.label} {...group} />
              ))}
            </div>
          </div>


        </div>

        {/* Right: form */}
        <div className="lg:w-3/5 bg-white p-12">
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FormField label="Full Name">
              <input value={formData.fullName}
                onChange={handleChange}
                name="fullName"
                className={inputClass} placeholder="John Doe" type="text" />
            </FormField>
            <FormField label="Email Address">
              <input value={formData.email}
                onChange={handleChange}
                name="email"
                className={inputClass} placeholder="john@company.com" type="email" />
            </FormField>
            <FormField label="Company Name">
              <input value={formData.company}
                onChange={handleChange}
                name="company"
                className={inputClass} placeholder="Acme Corp" type="text" />
            </FormField>
            <FormField label="Region">
              <select value={formData.region}
                onChange={handleChange}
                name="region"
                className={inputClass}>
                <option>India</option>
                <option>Canada</option>
                <option>USA</option>
                <option>Other</option>
              </select>
            </FormField>
            <div className="md:col-span-2">
              <FormField label="Service Interested In">
                <select value={formData.service}
                  onChange={handleChange}
                  name="service"
                  className={inputClass}>
                  <option>Bookkeeping</option>
                  <option>GST / TDS Filing</option>
                  <option>Canadian Tax (T1/T2)</option>
                  <option>Payroll</option>
                  <option>Financial Statements</option>
                  <option>Business Advisory</option>
                  <option>Other</option>
                </select>
              </FormField>
            </div>
            <div className="md:col-span-2">
              <FormField label="How Can We Help?">
                <textarea
                  value={formData.message}
                  onChange={handleChange}
                  name="message"
                  className={inputClass}
                  placeholder="Describe your financial goals..."
                  rows={4}
                />
              </FormField>
            </div>
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gold text-white font-manrope font-bold py-5 rounded-xl hover:-translate-y-0.5 transition-all shadow-gold-inset text-[16px] disabled:bg-gold/70 disabled:cursor-not-allowed"
              >
                {loading ? "Sending..." : "Send Inquiry"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
